import { useEffect, useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useFreshness, useKeyedWrite, useNow, useWrite } from "../api/hooks";
import type { AssignResult, CancellationResult, Dispatch, Incident, LocationInput, Proposal, Suggestions } from "../api/types";
import { can, canNarrowed, hasAnyRole, ROLE } from "../auth/roles";
import { ActionDialog } from "../components/ActionDialog";
import { Badge, StatusBadge } from "../components/Badges";
import { Dialog } from "../components/Dialog";
import { FileLinks, safeGeoUri, safeHttpsUrl } from "../components/Files";
import { PendingProposalLine, ProposalDialog, ProposedResult } from "../components/FourEyes";
import {
  ACCURACY_CLASS,
  APPLIANCE_STATUS,
  ACTIVE_INCIDENT_STATUSES,
  ASSIGN_OUTCOME,
  ASSIGN_REFUSAL,
  AUTH_KIND,
  CANCELLATION_REASON,
  CHANNEL,
  DECLINE_REASON,
  DISPATCH_FINAL,
  DISPATCH_STATUS,
  ESTIMATE_METHOD,
  INCIDENT_STATUS,
  LOCATION_SOURCE,
  MESSAGE_CHANNEL,
  MESSAGE_PATH,
  MESSAGE_STATUS,
  MILESTONE_SOURCE,
  RADIO_STATUSES,
  REPORT_STATUS,
  SEVERITY,
  SEVERITY_ORDER,
  SUGGESTION_ORIGIN,
  TIMELINE_KIND,
  TIMELINE_SOURCE,
  labelOf,
  optionsOf,
  statusOf,
} from "../components/labels";
import { Notice, ProblemAlert, QueryState, StaleNotice, UnknownOutcome, UnknownUnkeyed } from "../components/ProblemAlert";
import { useFireContext } from "../context";
import { formatDateTime, formatSeconds, lusakaLocalToUtc, secondsBetween } from "../format/datetime";
import { LocationPicker } from "./LocationPicker";
import { metres, personLabel, reasonError, Row, useConfigNames, useStationNames, useWardNames } from "./shared";
import { Button, IconButton, IconClose } from "@lcc/ui";

const isFinal = (d: Dispatch) => (DISPATCH_FINAL as readonly string[]).includes(d.status);

/** Whether any crew has reached the scene (a dispatch with an `on_scene` milestone, or the incident on scene). */
export function anyOnScene(inc: Incident): boolean {
  return inc.dispatches.some((d) => !!d.on_scene_at) || inc.status === "on_scene" || inc.status === "contained" || !!inc.first_on_scene_at;
}

export type CancelMode = "now" | "four_eyes" | "not_allowed";

/**
 * How a cancellation for this reason would work (UI only; the server decides and answers 403 `four_eyes_required`
 * or 409 `not_allowed_on_scene`): a duplicate takes effect at once until a crew is on scene, then needs a second
 * person; created in error or resolved before arrival take effect at once before the first dispatch, need a second
 * person after it, and are not possible once a crew is on scene.
 */
export function cancelMode(inc: Incident, category: string): CancelMode {
  const onScene = anyOnScene(inc);
  if (category === "duplicate_incident") return onScene ? "four_eyes" : "now";
  if (onScene) return "not_allowed";
  return inc.first_dispatch_at || inc.dispatches.length > 0 ? "four_eyes" : "now";
}

/** The live alert-to-dispatch clock: running until the first dispatch, then fixed. */
export function alertToDispatch(inc: { first_alert_at: string; first_dispatch_at?: string | null }, now: Date): { seconds: number | null; running: boolean } {
  if (inc.first_dispatch_at) return { seconds: secondsBetween(inc.first_alert_at, inc.first_dispatch_at), running: false };
  return { seconds: secondsBetween(inc.first_alert_at, now), running: true };
}

export function IncidentFlags({ inc }: { inc: { backup_requested: boolean; no_active_dispatch: boolean; no_alert?: boolean; accept_overdue?: boolean } }) {
  return (
    <span className="fer-badges">
      {inc.backup_requested && <Badge tone="warning" label="Backup requested" />}
      {inc.no_active_dispatch && <Badge tone="warning" label="No active dispatch" />}
      {inc.no_alert && <Badge tone="warning" label="No alert left (cancel or keep)" />}
      {inc.accept_overdue && <Badge tone="warning" label="A crew has not accepted in time" />}
    </span>
  );
}

// --------------------------------------------------------------------------------------------- suggestions + assign

interface AssignForm {
  selected: string[];
  /** Call signs of the ticked appliances as suggested (they leave the suggestions once dispatched). */
  callSigns: Record<string, string>;
  /** Capabilities of the ticked appliances as suggested (the Selected list shows them whatever is searched). */
  provides?: Record<string, string[]>;
  note: string;
  capabilities: string;
}

function SuggestAndAssign({ inc, onClose }: { inc: Incident; onClose: () => void }) {
  const { principal, refreshMs } = useFireContext();
  const names = useConfigNames();
  const write = useKeyedWrite((api, body: { appliance_ids: string[]; note?: string }, key) => endpoints.assignAppliances(api, inc.incident_id, body, key), `assign:${inc.incident_id}`);
  const kept = write.lockedForm as AssignForm | undefined;
  const [f, setF] = useState<AssignForm>(kept ?? { selected: [], callSigns: {}, note: "", capabilities: inc.backup_request && !inc.backup_request.acknowledged_at ? (inc.backup_request.capabilities ?? []).join(",") : "" });
  const [asked, setAsked] = useState<string[]>(f.capabilities ? f.capabilities.split(",").filter(Boolean) : []);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<AssignResult | null>(null);
  // Suggestions are computed at a moment (`computed_at`): they poll while the dialog is open, and "Suggest again" asks
  // the service again (review FW6).
  const q = useApi(["suggestions", inc.incident_id, asked], (api) => endpoints.getSuggestions(api, inc.incident_id, asked), { refetchInterval: refreshMs });
  const s: Suggestions | undefined = q.data;
  const locked = write.locked;
  // Live appliance status (review R2-1): a ticked appliance is unticked only when its own status says it can no longer
  // be sent (dispatched, out of service, off duty...), never because it is missing from suggestions filtered by other
  // capabilities. A locked request is never changed (it is resent exactly).
  const live = useApi(["live"], endpoints.getLiveMap, { enabled: can(principal, "viewLiveMap"), refetchInterval: refreshMs });
  const liveStatus = (id: string) => live.data?.appliances.find((x) => x.appliance_id === id)?.status;
  const [gone, setGone] = useState<string[]>([]);
  useEffect(() => {
    if (!live.data || locked) return;
    const unavailable = f.selected.filter((id) => {
      const st = liveStatus(id);
      return st !== undefined && st !== "available";
    });
    if (unavailable.length === 0) return;
    setGone((g) => [...g.filter((id) => !unavailable.includes(id)), ...unavailable]);
    setF((x) => ({ ...x, selected: x.selected.filter((id) => !unavailable.includes(id)) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- liveStatus reads live.data
  }, [live.data, locked, f.selected]);
  // The notice lists only those still unavailable and not ticked again: it clears when it no longer applies.
  const stillGone = gone.filter((id) => !f.selected.includes(id) && (liveStatus(id) ?? "available") !== "available");
  const suggestAgain = () => {
    const caps = f.capabilities ? f.capabilities.split(",").filter(Boolean) : [];
    if (caps.join(",") === asked.join(",")) void q.refetch();
    else setAsked(caps);
    void live.refetch();
  };
  const toggle = (id: string, call: string, provides: string[] = []) =>
    setF((x) => ({
      ...x,
      selected: x.selected.includes(id) ? x.selected.filter((y) => y !== id) : [...x.selected, id],
      callSigns: { ...x.callSigns, [id]: call },
      provides: { ...(x.provides ?? {}), [id]: provides },
    }));
  const callSign = (id: string) => f.callSigns[id] ?? s?.items.find((i) => i.appliance_id === id)?.call_sign ?? `appliance ${id.slice(0, 8)}`;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return write.mutate({ appliance_ids: [] }, { onSuccess: setResult });
    if (f.selected.length === 0) return setErr("Tick at least one appliance to send.");
    if (f.selected.length > 10) return setErr("Send at most 10 appliances at once.");
    setErr(null);
    write.remember(f);
    write.mutate(f.note.trim() ? { appliance_ids: f.selected, note: f.note.trim() } : { appliance_ids: f.selected }, { onSuccess: setResult });
  };

  return (
    <Dialog title={`Send appliances to ${inc.reference}`} onClose={onClose} focusContainer={locked}>
      {result ? (
        <div className="fer-form">
          <ul className="fer-plain-list" aria-label="Assignment results">
            {result.results.map((r) => (
              <li key={r.appliance_id}>
                <StatusBadge status={statusOf(ASSIGN_OUTCOME, r.outcome)} /> {r.dispatch?.call_sign ?? callSign(r.appliance_id)}
                {r.reason ? `: ${labelOf(ASSIGN_REFUSAL, r.reason)}` : ""}
              </li>
            ))}
          </ul>
          <div className="fer-actions">
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <form className="fer-form" onSubmit={submit} noValidate>
          <p className="fer-hint">
            Suggestions rank available, booked-on appliances by estimated travel time. They never send anyone: you choose.
          </p>
          <fieldset className="fer-bare-fieldset" disabled={locked}>
            <div className="fer-inline">
              <label className="fer-field">
                <span>Capabilities to look for (empty = the type's)</span>
                <select
                  multiple
                  value={f.capabilities ? f.capabilities.split(",").filter(Boolean) : []}
                  onChange={(e) => setF((x) => ({ ...x, capabilities: Array.from(e.target.selectedOptions, (o) => o.value).join(",") }))}
                >
                  {(names.staff?.capabilities ?? []).map((c) => (
                    <option key={c.key} value={c.key}>
                      {names.capability(c.key)}
                    </option>
                  ))}
                </select>
              </label>
              <Button variant="secondary" size="sm" onClick={suggestAgain} disabled={q.isFetching}>
                Suggest again
              </Button>
            </div>
          </fieldset>
          <QueryState isLoading={q.isLoading} error={q.error} label="suggestions" />
          {stillGone.length > 0 && (
            <Notice tone="warning">
              No longer available, so unticked: {stillGone.map((id) => `${callSign(id)} (${statusOf(APPLIANCE_STATUS, liveStatus(id) ?? "").label})`).join(", ")}.
            </Notice>
          )}
          <section aria-label="Selected appliances">
            <h4>Selected to send ({f.selected.length})</h4>
            {f.selected.length === 0 ? (
              <p className="fer-muted">None ticked yet.</p>
            ) : (
              <ul className="fer-plain-list">
                {f.selected.map((id) => (
                  <li key={id}>
                    <strong>{callSign(id)}</strong>
                    {f.provides?.[id]?.length ? ` – ${f.provides[id].map(names.capability).join(", ")}` : ""}{" "}
                    {!locked && (
                      <button type="button" className="fer-link" onClick={() => toggle(id, callSign(id), f.provides?.[id])} aria-label={`Untick ${callSign(id)}`}>
                        Untick
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
          {s && (
            <>
              <p>
                Needed: {s.required_capabilities.length ? s.required_capabilities.map(names.capability).join(", ") : "any appliance"} (worked out{" "}
                {formatDateTime(s.computed_at)}).
              </p>
              {s.missing_capabilities.length > 0 && (
                <Notice tone="warning">
                  No available appliance provides: {s.missing_capabilities.map(names.capability).join(", ")}. Consider mutual aid or a backup
                  from further away.
                </Notice>
              )}
              {s.items.length === 0 ? (
                <p className="fer-muted">No appliance is available now.</p>
              ) : (
                <div className="fer-scroll">
                  <table className="lcc-table" aria-label="Suggested appliances">
                    <thead>
                      <tr>
                        <th scope="col">Send</th>
                        <th scope="col">Rank</th>
                        <th scope="col">Appliance</th>
                        <th scope="col">Station</th>
                        <th scope="col">Provides</th>
                        <th scope="col">Travel time</th>
                        <th scope="col">Distance</th>
                        <th scope="col">Estimated</th>
                        <th scope="col">From</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.items.map((i) => (
                        <tr key={i.appliance_id}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Send ${i.call_sign}`}
                              checked={f.selected.includes(i.appliance_id)}
                              disabled={locked}
                              onChange={() => toggle(i.appliance_id, i.call_sign, i.provides)}
                            />
                          </td>
                          <td className="fer-num">{i.rank}</td>
                          <th scope="row">
                            {i.call_sign}
                            {i.water_capacity_litres ? <span className="fer-muted"> ({i.water_capacity_litres} l)</span> : null}
                          </th>
                          <td>{i.station_name ?? "—"}</td>
                          <td>{i.provides.length ? i.provides.map(names.capability).join(", ") : "—"}</td>
                          <td className="fer-num">{formatSeconds(i.travel_time_s)}</td>
                          <td className="fer-num">{metres(i.distance_m)}</td>
                          <td>{labelOf(ESTIMATE_METHOD, i.estimate_method)}</td>
                          <td>
                            {labelOf(SUGGESTION_ORIGIN, i.origin)}
                            {i.origin_age_seconds != null ? ` (${formatSeconds(i.origin_age_seconds)} old)` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {s.by_capability.length > 0 && (
                <ul className="fer-plain-list" aria-label="Nearest by capability">
                  {s.by_capability.map((c) => (
                    <li key={c.capability}>
                      Nearest {names.capability(c.capability)}: {c.appliance_ids.length ? c.appliance_ids.map(callSign).join(", ") : "none available"}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {can(principal, "assignAppliances") ? (
            <>
              <label className="fer-field">
                <span>Note to the crews (e.g. the access route)</span>
                <textarea rows={2} maxLength={1000} value={f.note} disabled={locked} onChange={(e) => setF({ ...f, note: e.target.value })} />
              </label>
              {locked && f.selected.length > 0 && <p>Locked request: send {f.selected.map(callSign).join(", ")}.</p>}
              {err && (
                <p className="fer-form-error" role="alert">
                  {err}
                </p>
              )}
              <ProblemAlert error={write.error} />
              <UnknownOutcome write={write} retry={() => write.mutate({ appliance_ids: [] }, { onSuccess: setResult })} />
            </>
          ) : (
            <Notice>Dispatchers send appliances; you can read the suggestions.</Notice>
          )}
          <div className="fer-actions">
            <Button variant="secondary" onClick={onClose}>
              Back
            </Button>
            {can(principal, "assignAppliances") && (
              <Button type="submit" disabled={write.isPending}>
                Send the ticked appliances
              </Button>
            )}
          </div>
        </form>
      )}
    </Dialog>
  );
}

// --------------------------------------------------------------------------------------------- cancellation

function CancelDialog({ inc, onClose }: { inc: Incident; onClose: () => void }) {
  const { principal } = useFireContext();
  const names = useConfigNames();
  const others = useApi(["incidents", { active: true }], (api) => endpoints.listIncidents(api));
  const w = useWrite((api, body: Record<string, unknown>) => endpoints.cancelIncident(api, inc.incident_id, body as never));
  const [category, setCategory] = useState("");
  const [dup, setDup] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<CancellationResult | null>(null);
  const mode = category ? cancelMode(inc, category) : null;
  const mayPropose = hasAnyRole(principal, [ROLE.dispatcher]);
  const candidates = (others.data ?? []).filter(
    (i) => i.incident_id !== inc.incident_id && (ACTIVE_INCIDENT_STATUSES as readonly string[]).includes(i.status) && i.status !== "cleared",
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!category) return setErr("Choose why the incident is cancelled.");
    if (mode === "not_allowed") return setErr("A crew is on scene: only a duplicate can be cancelled; otherwise the incident closes through its report.");
    if (category === "duplicate_incident" && !dup) return setErr("Choose the incident this one duplicates.");
    const e1 = reasonError(reason);
    if (e1) return setErr(e1);
    if (mode === "four_eyes" && !mayPropose) return setErr("This cancellation needs a dispatcher's proposal; you confirm it in Approvals.");
    setErr(null);
    const body: Record<string, unknown> = { action: mode === "now" ? "cancel" : "propose", reason_category: category, reason: reason.trim() };
    if (category === "duplicate_incident") body.duplicate_of = dup;
    w.mutate(body, { onSuccess: setDone });
  };

  return (
    <Dialog title={`Cancel incident ${inc.reference}`} onClose={onClose}>
      {done ? (
        <div className="fer-form">
          {done.result === "incident" ? (
            <p role="status" className="fer-success">
              <span aria-hidden="true">✓ </span>
              The incident is cancelled.
              {done.incident.cancellation?.dispatches_moved ? ` ${done.incident.cancellation.dispatches_moved} dispatch(es) moved to the other incident.` : ""}
            </p>
          ) : (
            <ProposedResult proposal={done.proposal} />
          )}
          <div className="fer-actions">
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <form className="fer-form" onSubmit={submit} noValidate>
          <label className="fer-field">
            <span>Why (required)</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Choose…</option>
              {optionsOf(CANCELLATION_REASON).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          {category === "duplicate_incident" && (
            <label className="fer-field">
              <span>The incident it duplicates (required)</span>
              <select value={dup} onChange={(e) => setDup(e.target.value)}>
                <option value="">Choose…</option>
                {candidates.map((i) => (
                  <option key={i.incident_id} value={i.incident_id}>
                    {i.reference} – {names.incidentType(i.incident_type)} ({statusOf(INCIDENT_STATUS, i.status).label})
                  </option>
                ))}
              </select>
            </label>
          )}
          {mode === "now" && (
            <Notice>
              {category === "duplicate_incident"
                ? "This takes effect at once: the alerts and every dispatch move to the other incident (no crew is stood down; crews are told the new reference)."
                : "This takes effect at once (nobody has been dispatched)."}
            </Notice>
          )}
          {mode === "four_eyes" && (
            <Notice tone="warning">
              {mayPropose
                ? "This needs a second person: it is proposed now and takes effect when the chief or another dispatcher confirms it. Meanwhile you may stand crews down."
                : "This needs a dispatcher's proposal and a second person's confirmation; you confirm proposals in Approvals."}
            </Notice>
          )}
          {mode === "not_allowed" && (
            <Notice tone="warning">A crew has reached the scene: only a duplicate can be cancelled. The incident closes through its report.</Notice>
          )}
          <label className="fer-field">
            <span>Reason (at least 3 characters)</span>
            <textarea rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          {err && (
            <p className="fer-form-error" role="alert">
              {err}
            </p>
          )}
          <ProblemAlert error={w.error} />
          <UnknownUnkeyed show={w.unknown} />
          <div className="fer-actions">
            <Button variant="secondary" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={w.isPending || mode === "not_allowed"}>
              {mode === "four_eyes" ? "Propose the cancellation" : "Cancel the incident"}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

// --------------------------------------------------------------------------------------------- change the incident

function UpdateDialog({ inc, onClose }: { inc: Incident; onClose: () => void }) {
  const names = useConfigNames();
  const [type, setType] = useState(inc.incident_type);
  const [severity, setSeverity] = useState<string>(inc.severity);
  const [place, setPlace] = useState(inc.place_description ?? "");
  const [loc, setLoc] = useState<LocationInput | null>(null);
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const w = useWrite((api, body: Record<string, unknown>) => endpoints.updateIncident(api, inc.incident_id, body as never));
  const lower = SEVERITY_ORDER.indexOf(severity as never) < SEVERITY_ORDER.indexOf(inc.severity as never);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, unknown> = {};
    if (type !== inc.incident_type) body.incident_type = type;
    if (severity !== inc.severity) body.severity = severity;
    if (place !== (inc.place_description ?? "")) body.place_description = place.trim() || null;
    if (loc) body.location = loc;
    if (lower && !note.trim()) return setErr("Lowering the severity needs a note saying why.");
    if (note.trim()) body.note = note.trim();
    if (Object.keys(body).filter((k) => k !== "note").length === 0) return setErr("Nothing has changed.");
    setErr(null);
    w.mutate(body, { onSuccess: onClose });
  };
  return (
    <Dialog title={`Change incident ${inc.reference}`} onClose={onClose}>
      <form className="fer-form" onSubmit={submit} noValidate>
        <label className="fer-field">
          <span>Type</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {(names.staff?.incident_types ?? [{ key: inc.incident_type }]).map((t) => (
              <option key={t.key} value={t.key}>
                {names.incidentType(t.key)}
              </option>
            ))}
          </select>
        </label>
        <label className="fer-field">
          <span>Severity</span>
          <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITY_ORDER.map((s) => (
              <option key={s} value={s}>
                {SEVERITY[s].label}
              </option>
            ))}
          </select>
        </label>
        <label className="fer-field">
          <span>What the caller said about the place</span>
          <textarea rows={2} maxLength={500} value={place} onChange={(e) => setPlace(e.target.value)} />
        </label>
        <h4>New location (optional; the previous one stays in the timeline)</h4>
        <LocationPicker value={loc} onChange={setLoc} />
        <label className="fer-field">
          <span>{lower ? "Note (required: why the severity goes down)" : "Note"}</span>
          <textarea rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        {err && (
          <p className="fer-form-error" role="alert">
            {err}
          </p>
        )}
        <ProblemAlert error={w.error} />
        <UnknownUnkeyed show={w.unknown} />
        <div className="fer-actions">
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" disabled={w.isPending}>
            Save the changes
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DispatchDialog({ dispatchId, onClose }: { dispatchId: string; onClose: () => void }) {
  const { principal } = useFireContext();
  const q = useApi(["dispatch", dispatchId], (api) => endpoints.getDispatch(api, dispatchId));
  const d = q.data;
  return (
    <Dialog title={d ? `Dispatch of ${d.call_sign}` : "Dispatch"} onClose={onClose}>
      <QueryState isLoading={q.isLoading} error={q.error} label="the dispatch" />
      {d && (
        <div className="fer-form">
          <dl className="fer-dl">
            <Row term="Status">
              <StatusBadge status={statusOf(DISPATCH_STATUS, d.status)} />
            </Row>
            <Row term="Crew commander">{d.commander_name ?? "—"}</Row>
            <Row term="Assigned">
              {formatDateTime(d.assigned_at)} by {personLabel(d.assigned_by, principal.partyId)}
            </Row>
            {d.decline_reason_category && (
              <Row term="Declined">
                {labelOf(DECLINE_REASON, d.decline_reason_category)}: {d.decline_reason}
              </Row>
            )}
            {d.stand_down_reason && <Row term="Stood down">{d.stand_down_reason}</Row>}
            {d.note && <Row term="Note to the crew">{d.note}</Row>}
          </dl>
          <div className="fer-scroll">
            <table className="lcc-table" aria-label="Milestones">
              <thead>
                <tr>
                  <th scope="col">Status</th>
                  <th scope="col">At</th>
                  <th scope="col">Recorded</th>
                  <th scope="col">From</th>
                </tr>
              </thead>
              <tbody>
                {d.milestones.map((m, i) => (
                  <tr key={i}>
                    <th scope="row">{statusOf(DISPATCH_STATUS, m.status).label}</th>
                    <td>{formatDateTime(m.at)}</td>
                    <td>{formatDateTime(m.recorded_at)}</td>
                    <td>
                      {labelOf(MILESTONE_SOURCE, m.source)}
                      {m.auth === "local_auth" ? " (local sign-in)" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h4>Messages to the crew</h4>
          <ul className="fer-plain-list">
            {d.messages.map((m, i) => (
              <li key={i}>
                {labelOf(MESSAGE_CHANNEL, m.channel)} by {labelOf(MESSAGE_PATH, m.path)}: <StatusBadge status={statusOf(MESSAGE_STATUS, m.status)} /> ({formatDateTime(m.at)})
              </li>
            ))}
          </ul>
          <div className="fer-actions">
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// --------------------------------------------------------------------------------------------- the detail

type DialogState =
  | { kind: "assign" }
  | { kind: "cancel" }
  | { kind: "update" }
  | { kind: "escalate" }
  | { kind: "contain" }
  | { kind: "radio"; dispatch: Dispatch }
  | { kind: "standDown"; dispatch: Dispatch }
  | { kind: "dispatch"; id: string }
  | { kind: "proposal"; proposal: Proposal }
  | null;

/**
 * One incident: location (and more accurate positions to adopt), the live clocks, dispatches with their messages and
 * estimates, the timeline and linked alerts (never callers). The dispatcher suggests and assigns appliances, records
 * crew statuses from the radio, stands crews down, escalates or acknowledges backup, marks it contained and cancels it
 * (at once or four-eyes).
 */
export function IncidentDetail({ incidentId, onClose, onReport }: { incidentId: string; onClose?: () => void; onReport?: () => void }) {
  const { principal, refreshMs } = useFireContext();
  const now = useNow();
  const names = useConfigNames();
  const wardName = useWardNames();
  const stationName = useStationNames();
  const q = useApi(["incident", incidentId], (api) => endpoints.getIncident(api, incidentId), { refetchInterval: refreshMs });
  const proposals = useApi(["approvals", "incident_cancellation", "pending"], (api) => endpoints.listApprovals(api, { mine: "all", kind: "incident_cancellation", status: "pending" }), {
    enabled: can(principal, "viewApprovals") && hasAnyRole(principal, [ROLE.dispatcher, ROLE.chief]),
  });
  const adopt = useWrite((api, fixId: string) => endpoints.updateIncident(api, incidentId, { fix_id: fixId }));
  const ack = useWrite((api) => endpoints.updateIncident(api, incidentId, { backup_requested: false }));
  const [dialog, setDialog] = useState<DialogState>(null);
  const inc = q.data;
  const fresh = useFreshness(q);
  if (!inc) return <QueryState isLoading={q.isLoading} error={q.error} label="the incident" />;

  const final = inc.status === "cleared" || inc.status === "closed" || inc.status === "cancelled";
  // A stale incident's clocks stop at its last refresh (review FW1): they never claim to be live.
  const clockNow = fresh.stale && fresh.refreshedAt ? fresh.refreshedAt : now;
  const clock = alertToDispatch(inc, clockNow);
  const toScene = inc.first_dispatch_at ? secondsBetween(inc.first_dispatch_at, inc.first_on_scene_at ?? clockNow) : null;
  const pending = (proposals.data?.items ?? []).filter((p) => p.subject_id === inc.incident_id);
  const maps = safeHttpsUrl(inc.navigation?.maps_url);
  const geo = safeGeoUri(inc.navigation?.geo_uri);
  const activeCount = inc.dispatches.filter((d) => !isFinal(d)).length;

  return (
    <section className="fer-panel fer-record" aria-label={`Incident ${inc.reference}`}>
      <div className="lcc-detail__head">
        <div className="lcc-detail__titles">
          <p className={`lcc-eyebrow${inc.severity === "critical" ? " lcc-eyebrow--emergency" : ""}`}>
            Incident · {inc.location.in_service_area ? wardName(inc.location.ward_id) : "outside every ward"}
          </p>
          <h3 className="lcc-detail__title">
            {inc.reference}: {names.incidentType(inc.incident_type)}
          </h3>
          <span className="fer-badges fer-record__status">
            <StatusBadge status={statusOf(SEVERITY, inc.severity)} />
            <StatusBadge status={statusOf(INCIDENT_STATUS, inc.status)} />
          </span>
        </div>
        {onClose && <IconButton label="Close" size="sm" icon={<IconClose size={16} />} onClick={onClose} />}
      </div>
      <StaleNotice {...fresh} error={q.error} what="incident" />
      <IncidentFlags inc={inc} />
      <p className="fer-clock" role="timer" aria-live="off">
        Alert to dispatch: {formatSeconds(clock.seconds)} {clock.running ? (fresh.stale ? "(at least – stopped: not refreshed)" : "(running – nobody dispatched yet)") : ""}
        {inc.first_dispatch_at && (
          <>
            {" "}
            · Dispatch to scene: {formatSeconds(toScene)} {inc.first_on_scene_at ? "" : fresh.stale ? "(stopped: not refreshed)" : "(running)"}
          </>
        )}
      </p>
      <ProblemAlert error={adopt.error ?? ack.error} />
      <UnknownUnkeyed show={adopt.unknown || ack.unknown} />
      {pending.map((p) => (
        <PendingProposalLine key={p.proposal_id} proposal={p} onOpen={() => setDialog({ kind: "proposal", proposal: p })} />
      ))}

      <div className="fer-actions fer-actions--start" role="group" aria-label="Incident actions">
        {!final && can(principal, "viewSuggestions") && (
          <Button onClick={() => setDialog({ kind: "assign" })}>
            {can(principal, "assignAppliances") ? "Suggest and send appliances" : "See suggested appliances"}
          </Button>
        )}
        {!final && canNarrowed(principal, "escalateFromConsole") && (
          <Button variant="secondary" onClick={() => setDialog({ kind: "escalate" })}>
            Escalate or request backup
          </Button>
        )}
        {inc.status === "on_scene" && canNarrowed(principal, "containFromConsole") && (
          <Button variant="secondary" onClick={() => setDialog({ kind: "contain" })}>
            Mark contained
          </Button>
        )}
        {!final && can(principal, "updateIncident") && (
          <Button variant="secondary" onClick={() => setDialog({ kind: "update" })}>
            Change type, severity or location
          </Button>
        )}
        {!final && can(principal, "cancelIncident") && (
          <Button variant="secondary" onClick={() => setDialog({ kind: "cancel" })}>
            Cancel the incident
          </Button>
        )}
        {onReport && can(principal, "viewReport") && (inc.report_status !== "none" || inc.status === "cleared") && (
          <Button variant="secondary" onClick={onReport}>
            Incident report ({statusOf(REPORT_STATUS, inc.report_status).label})
          </Button>
        )}
      </div>

      {inc.backup_request && !inc.backup_request.acknowledged_at && (
        <Notice tone="warning">
          <p>
            <strong>Backup requested</strong> {formatDateTime(inc.backup_request.requested_at)} by {personLabel(inc.backup_request.requested_by, principal.partyId)}
            {inc.backup_request.requested_role ? ` (${inc.backup_request.requested_role})` : ""}: {inc.backup_request.appliances} more appliance(s)
            {inc.backup_request.capabilities?.length ? ` with ${inc.backup_request.capabilities.map(names.capability).join(", ")}` : ""}
            {inc.backup_request.note ? ` – ${inc.backup_request.note}` : ""}.
          </p>
          {can(principal, "updateIncident") && (
            <Button size="sm" disabled={ack.isPending} onClick={() => ack.mutate(undefined)}>
              Acknowledge the backup request (sent)
            </Button>
          )}
        </Notice>
      )}

      <h4>Location</h4>
      <p>
        <StatusBadge status={statusOf(ACCURACY_CLASS, inc.location.accuracy_class)} /> {labelOf(LOCATION_SOURCE, inc.location.source)}, within{" "}
        {metres(inc.location.accuracy_m)}; Plus Code {inc.location.plus_code}; {inc.location.in_service_area ? wardName(inc.location.ward_id) : "outside every ward (mutual aid)"}
        {inc.place_description ? ` – “${inc.place_description}”` : ""}
      </p>
      <p>
        {maps ? (
          <a href={maps} target="_blank" rel="noopener noreferrer">
            Open in maps
          </a>
        ) : (
          <span className="fer-muted">No secure maps link</span>
        )}
        {geo ? <span className="fer-hint"> · Navigation: {geo}</span> : null}
      </p>
      {inc.pending_fixes.length > 0 && (
        <div className="fer-scroll">
          <table className="lcc-table" aria-label="More accurate positions received">
            <thead>
              <tr>
                <th scope="col">From</th>
                <th scope="col">Within</th>
                <th scope="col">Plus Code</th>
                <th scope="col">Received</th>
                <th scope="col">
                  <span className="lcc-sr-only">Adopt</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {inc.pending_fixes.map((x) => (
                <tr key={x.fix_id}>
                  <th scope="row">{labelOf(LOCATION_SOURCE, x.source)}</th>
                  <td>{metres(x.accuracy_m)}</td>
                  <td>{x.plus_code}</td>
                  <td>{formatDateTime(x.received_at)}</td>
                  <td>
                    {!final && can(principal, "updateIncident") && (
                      <Button variant="secondary" size="sm" disabled={adopt.isPending} onClick={() => adopt.mutate(x.fix_id)}>
                        Use this position
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h4>
        Dispatches ({activeCount} active of {inc.dispatches.length})
      </h4>
      {inc.dispatches.length === 0 ? (
        <p className="fer-muted">Nobody has been sent yet.</p>
      ) : (
        <div className="fer-scroll">
          <table className="lcc-table" aria-label="Dispatches">
            <thead>
              <tr>
                <th scope="col">Appliance</th>
                <th scope="col">Station</th>
                <th scope="col">Status</th>
                <th scope="col">Commander</th>
                <th scope="col">Assigned</th>
                <th scope="col">ETA</th>
                <th scope="col">Crew told</th>
                <th scope="col">
                  <span className="lcc-sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {inc.dispatches.map((d) => (
                <tr key={d.dispatch_id}>
                  <th scope="row">
                    {d.call_sign}
                    {d.moved_from_incident_id ? <span className="fer-muted"> (moved in)</span> : null}
                  </th>
                  <td>{stationName(d.station_id)}</td>
                  <td>
                    <StatusBadge status={statusOf(DISPATCH_STATUS, d.status)} />
                    {d.accept_overdue && <Badge tone="warning" label="Not accepted in time" />}
                  </td>
                  <td>{d.commander_name ?? "—"}</td>
                  <td>{formatDateTime(d.assigned_at)}</td>
                  <td>
                    {d.eta_minutes != null ? `${d.eta_minutes} min` : "—"}
                    {d.estimate_method ? <span className="fer-hint"> ({labelOf(ESTIMATE_METHOD, d.estimate_method)})</span> : null}
                  </td>
                  <td>
                    {d.messages.length === 0
                      ? "—"
                      : d.messages.map((m, i) => (
                          <span key={i}>
                            {labelOf(MESSAGE_CHANNEL, m.channel)}: {statusOf(MESSAGE_STATUS, m.status).label}
                            {i < d.messages.length - 1 ? "; " : ""}
                          </span>
                        ))}
                  </td>
                  <td className="fer-row-actions">
                    {!isFinal(d) && canNarrowed(principal, "recordRadioStatus") && (
                      <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "radio", dispatch: d })} aria-label={`Radio status of ${d.call_sign}`}>
                        Radio status
                      </Button>
                    )}
                    {!isFinal(d) && can(principal, "standDown") && (
                      <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "standDown", dispatch: d })} aria-label={`Stand down ${d.call_sign}`}>
                        Stand down
                      </Button>
                    )}
                    {can(principal, "viewDispatch") && (
                      <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "dispatch", id: d.dispatch_id })} aria-label={`Details of ${d.call_sign}`}>
                        Details
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h4>Alerts ({inc.alerts.length})</h4>
      {inc.alerts.length === 0 ? (
        <p className="fer-muted">No alert is linked any more.</p>
      ) : (
        <ul className="fer-plain-list" aria-label="Linked alerts">
          {inc.alerts.map((a) => (
            <li key={a.alert_id}>
              {a.reference} – {statusOf(CHANNEL, a.channel).label}, {formatDateTime(a.received_at)}
              {a.accuracy_class ? `, ${statusOf(ACCURACY_CLASS, a.accuracy_class).label.toLowerCase()}` : ""}
              {a.photo_file_ids && a.photo_file_ids.length > 0 && <FileLinks ids={a.photo_file_ids} noun="Scene photo" />}
            </li>
          ))}
        </ul>
      )}
      {inc.cancellation && (
        <Notice>
          Cancelled {formatDateTime(inc.cancellation.at)}: {labelOf(CANCELLATION_REASON, inc.cancellation.reason_category)} – {inc.cancellation.reason}
          {inc.cancellation.confirmed_by ? ` (confirmed by ${personLabel(inc.cancellation.confirmed_by, principal.partyId)})` : ""}
        </Notice>
      )}

      <h4>Timeline</h4>
      <ol className="fer-timeline" aria-label="Timeline">
        {inc.timeline.map((t, i) => (
          <li key={i}>
            <span className="fer-num">{formatDateTime(t.at)}</span> {labelOf(TIMELINE_KIND, t.kind)} ({labelOf(TIMELINE_SOURCE, t.source)}
            {t.auth === "local_auth" ? `, ${AUTH_KIND.local_auth}` : ""}){t.detail ? `: ${t.detail}` : ""}
            {t.recorded_at && t.recorded_at !== t.at ? <span className="fer-hint"> (recorded {formatDateTime(t.recorded_at)})</span> : null}
          </li>
        ))}
      </ol>

      {dialog?.kind === "assign" && <SuggestAndAssign inc={inc} onClose={() => setDialog(null)} />}
      {dialog?.kind === "cancel" && <CancelDialog inc={inc} onClose={() => setDialog(null)} />}
      {dialog?.kind === "update" && <UpdateDialog inc={inc} onClose={() => setDialog(null)} />}
      {dialog?.kind === "dispatch" && <DispatchDialog dispatchId={dialog.id} onClose={() => setDialog(null)} />}
      {dialog?.kind === "proposal" && <ProposalDialog proposal={dialog.proposal} onClose={() => setDialog(null)} />}
      {dialog?.kind === "escalate" && (
        <ActionDialog
          title={`Escalate ${inc.reference}`}
          intro={<p>Raise the severity (never lower here), request more appliances, or both.</p>}
          fields={[
            {
              name: "severity",
              label: "New severity",
              kind: "select",
              options: SEVERITY_ORDER.filter((s) => SEVERITY_ORDER.indexOf(s) > SEVERITY_ORDER.indexOf(inc.severity as never)).map((s) => [s, SEVERITY[s].label] as const),
            },
            { name: "appliances", label: "More appliances needed (1-10)", kind: "number", min: "1", max: "10" },
            {
              name: "capabilities",
              label: "With capability",
              kind: "select",
              options: (names.staff?.capabilities ?? []).map((c) => [c.key, names.capability(c.key)] as const),
            },
            { name: "note", label: "Note", kind: "textarea", maxLength: 1000 },
          ]}
          submitLabel="Escalate"
          build={(v) => {
            const body: Record<string, unknown> = {};
            if (v.severity) body.severity = v.severity;
            if (v.appliances) {
              const n = Number(v.appliances);
              if (!Number.isInteger(n) || n < 1 || n > 10) return { error: "More appliances: a whole number from 1 to 10." };
              body.backup = { appliances: n, capabilities: v.capabilities ? [v.capabilities] : [] };
            }
            if (!body.severity && !body.backup) return { error: "Raise the severity, request appliances, or both." };
            if (v.note?.trim()) body.note = v.note.trim();
            return { body };
          }}
          write={(api, body) => endpoints.escalateIncident(api, inc.incident_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "contain" && (
        <ActionDialog
          title={`Mark ${inc.reference} contained`}
          intro={<p>Once every crew is released or stood down, the incident becomes cleared and its report can be submitted.</p>}
          fields={[
            { name: "captured_at", label: "Contained at (Lusaka time; empty = now)", kind: "datetime" },
            { name: "note", label: "Note", kind: "textarea", maxLength: 2000 },
          ]}
          submitLabel="Mark contained"
          build={(v) => {
            const body: Record<string, unknown> = {};
            if (v.captured_at) {
              const at = lusakaLocalToUtc(v.captured_at);
              if (!at) return { error: "Give the time as a date and time." };
              body.captured_at = at;
            }
            if (v.note?.trim()) body.note = v.note.trim();
            return { body };
          }}
          write={(api, body) => endpoints.containIncident(api, inc.incident_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "radio" && (
        <ActionDialog
          title={`Radio status of ${dialog.dispatch.call_sign}`}
          intro={<p>Now: {statusOf(DISPATCH_STATUS, dialog.dispatch.status).label}. A status never goes backwards; an earlier time is kept for a milestone already passed.</p>}
          fields={[
            { name: "status", label: "Status heard on the radio", kind: "select", required: true, options: RADIO_STATUSES.map((s) => [s, DISPATCH_STATUS[s].label] as const) },
            { name: "captured_at", label: "Heard at (Lusaka time; empty = now)", kind: "datetime" },
            { name: "note", label: "Note", kind: "textarea", maxLength: 1000 },
          ]}
          submitLabel="Record the status"
          build={(v) => {
            const body: Record<string, unknown> = { status: v.status };
            if (v.captured_at) {
              const at = lusakaLocalToUtc(v.captured_at);
              if (!at) return { error: "Give the time as a date and time." };
              body.captured_at = at;
            }
            if (v.note?.trim()) body.note = v.note.trim();
            return { body };
          }}
          write={(api, body) => endpoints.recordDispatchStatus(api, dialog.dispatch.dispatch_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "standDown" && (
        <ActionDialog
          title={`Stand down ${dialog.dispatch.call_sign}`}
          intro={<p>The crew is told by push and SMS, and the appliance becomes available again.</p>}
          fields={[{ name: "reason", label: "Reason (at least 3 characters)", kind: "textarea", required: true, maxLength: 1000 }]}
          submitLabel="Stand down"
          build={(v) => {
            const e = reasonError(v.reason ?? "");
            return e ? { error: e } : { body: { reason: v.reason.trim() } };
          }}
          write={(api, body) => endpoints.standDown(api, dialog.dispatch.dispatch_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}
